// src/provider/authProvider.jsx
import axios from "axios";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import SedeSessionNotice from "../components/SedeSessionNotice";
import FmReminderModal from "../components/FmReminderModal";
import SessionExpiryModal from "../components/SessionExpiryModal";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const API = process.env.REACT_APP_API;
const SEDE_POLL_INTERVAL_MS = 30000;
const SESSION_WARNING_SECONDS = 10 * 60;
const LOGIN_PATH = "/evaluation-system-verify/login";

export default function AuthProvider({ children }) {
    // 🔐 Estado de autenticación
    const [token, setTokenState] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // 👤 Datos de usuario (solo en memoria)
    const [user, setUser] = useState(null);
    const [name, setName] = useState(null);
    const [fullName, setFullName] = useState(null);
    const [rol, setRolState] = useState(null);

    // 🏬 Sede (solo en memoria)
    const [sedeId, setSedeId] = useState(null);
    const [sedeName, setSedeName] = useState(null);
    const [sedeLocation, setSedeLocation] = useState(null);
    const sedeSnapshotRef = useRef({ sedeId: null, sedeName: null, sedeLocation: null });

    // 🔔 Popup global de sede
    const [sedeNotice, setSedeNotice] = useState(null);
    // Recordatorio FM: se consulta únicamente después de cerrar el aviso de sede del login.
    const [fmReminder, setFmReminder] = useState(null);

    // ⏱️ Aviso de expiración JWT (10 minutos antes)
    const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
    const [sessionRemainingSeconds, setSessionRemainingSeconds] = useState(0);
    const [sessionRenewing, setSessionRenewing] = useState(false);
    const sessionWarningDismissedForExpRef = useRef(null);

    const setToken = (t) => {
        setTokenState(t ?? null);
        if (t) {
            localStorage.setItem("token", t);
            axios.defaults.headers.common["Authorization"] = "Bearer " + t;
        } else {
            localStorage.removeItem("token");
            delete axios.defaults.headers.common["Authorization"];
        }
    };

    const setRol = (r) => setRolState(r != null ? String(r) : null);

    const applySede = useCallback((data = {}) => {
        const nextSedeId = data?.sedeId ?? null;
        const nextSedeName = data?.sedeName ?? null;
        const nextSedeLocation = data?.sedeLocation ?? null;

        setSedeId(nextSedeId);
        setSedeName(nextSedeName);
        setSedeLocation(nextSedeLocation);
        sedeSnapshotRef.current = {
            sedeId: nextSedeId,
            sedeName: nextSedeName,
            sedeLocation: nextSedeLocation,
        };
    }, []);

    const showSedeLoginNotice = useCallback((payload = {}) => {
        setSedeNotice({
            type: "login",
            fullName: payload.fullName ?? payload.name ?? null,
            name: payload.name ?? null,
            sedeId: payload.sedeId ?? null,
            sedeName: payload.sedeName ?? null,
            sedeLocation: payload.sedeLocation ?? null,
        });
    }, []);

    const closeFmReminder = useCallback(() => setFmReminder(null), []);

    const closeSedeNotice = useCallback(async () => {
        const wasLoginNotice = sedeNotice?.type === "login";
        setSedeNotice(null);

        // El popup de sede no se modifica. Al terminar ese flujo, consultamos el aviso FM.
        if (!wasLoginNotice || String(rol) !== "3") return;
        const t = localStorage.getItem("token");
        if (!t) return;

        try {
            const { data } = await axios.get(`${API}/fms/reminders`, {
                headers: { Authorization: `Bearer ${t}` },
                withCredentials: true,
            });
            if (Number(data?.data?.total || 0) > 0) setFmReminder(data.data);
        } catch (error) {
            // El recordatorio nunca debe bloquear el ingreso al sistema.
            console.warn("[FM reminder] No se pudo consultar el recordatorio", error);
        }
    }, [sedeNotice, rol]);

    const logout = useCallback(() => {
        setToken(null);
        setUser(null);
        setName(null);
        setFullName(null);
        setRol(null);
        setSedeId(null);
        setSedeName(null);
        setSedeLocation(null);
        sedeSnapshotRef.current = { sedeId: null, sedeName: null, sedeLocation: null };
        setSedeNotice(null);
        setFmReminder(null);
        setSessionWarningOpen(false);
        setSessionRemainingSeconds(0);
        setSessionRenewing(false);
        sessionWarningDismissedForExpRef.current = null;
    }, []);

    // ===== Hidratación inicial (solo token) =====
    useEffect(() => {
        const t = localStorage.getItem("token");
        if (t) {
            setTokenState(t);
            axios.defaults.headers.common["Authorization"] = "Bearer " + t;
        }
        setIsAuthReady(true);
    }, []);

    // ===== Cargar rol/user/name desde backend cuando haya token =====
    useEffect(() => {
        if (!token) {
            setUser(null);
            setName(null);
            setFullName(null);
            setRol(null);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/user-role`, {
                    withCredentials: true,
                });
                if (cancelled) return;

                setRol(data?.rol != null ? String(data.rol) : null);
                setUser(data?.username ?? null);
                setName(data?.name ?? null);
                setFullName(data?.fullName ?? data?.name ?? null);
            } catch (error) {
                if (cancelled) return;
                setRol(null);
                setUser(null);
                setName(null);
                setFullName(null);
            }
        })();

        return () => { cancelled = true; };
    }, [token]);

    // ===== Cargar sede actual =====
    const refreshSede = useCallback(async () => {
        const t = localStorage.getItem("token");
        if (!t) return null;

        try {
            const { data } = await axios.get(`${API}/context/sede`, {
                headers: { Authorization: `Bearer ${t}` },
                withCredentials: true,
            });
            applySede(data);
            return data;
        } catch {
            applySede({ sedeId: null, sedeName: null, sedeLocation: null });
            return null;
        }
    }, [applySede]);

    useEffect(() => {
        if (token) refreshSede();
    }, [token, refreshSede]);

    // ===== Detectar cambio de sede mientras el asesor sigue logueado =====
    useEffect(() => {
        if (!token || String(rol) !== "3") return undefined;

        let cancelled = false;
        let running = false;

        const checkAssignedSede = async () => {
            if (running || cancelled) return;
            running = true;

            try {
                const { data } = await axios.get(`${API}/context/sede`, {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true,
                });
                if (cancelled) return;

                const previous = sedeSnapshotRef.current;
                const nextSedeId = data?.sedeId ?? null;
                const hasPrevious = previous?.sedeId != null;
                const changed = hasPrevious && String(previous.sedeId) !== String(nextSedeId);

                if (changed) {
                    setSedeNotice({
                        type: "change",
                        fullName: fullName ?? name ?? user,
                        name,
                        previousSedeId: previous.sedeId,
                        previousSedeName: previous.sedeName,
                        sedeId: nextSedeId,
                        sedeName: data?.sedeName ?? null,
                        sedeLocation: data?.sedeLocation ?? null,
                    });
                }

                applySede(data);
            } catch (error) {
                // No se limpia la sede por un fallo transitorio de red.
                // Los interceptores globales ya manejan 401/419.
            } finally {
                running = false;
            }
        };

        const intervalId = window.setInterval(checkAssignedSede, SEDE_POLL_INTERVAL_MS);

        // También validar cuando la pestaña vuelve a estar visible.
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") checkAssignedSede();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [token, rol, fullName, name, user, applySede]);

    // ======= ⏱️ Vigilar vencimiento real del JWT =======
    const decodeJwtPayload = useCallback((jwtToken) => {
        try {
            const payload = jwtToken.split(".")[1];
            if (!payload) return null;
            const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
            const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
            return JSON.parse(decodeURIComponent(escape(window.atob(padded))));
        } catch {
            return null;
        }
    }, []);

    const redirectToLogin = useCallback(() => {
        logout();
        if (!window.location.pathname.startsWith("/evaluation-system-verify")) {
            window.location.replace(LOGIN_PATH);
        } else if (window.location.pathname !== LOGIN_PATH) {
            window.location.replace(LOGIN_PATH);
        }
    }, [logout]);

    useEffect(() => {
        if (!token) {
            setSessionWarningOpen(false);
            setSessionRemainingSeconds(0);
            return undefined;
        }

        const payload = decodeJwtPayload(token);
        const exp = Number(payload?.exp);
        if (!Number.isFinite(exp)) {
            redirectToLogin();
            return undefined;
        }

        const evaluateExpiration = () => {
            const nowSeconds = Math.floor(Date.now() / 1000);
            const remaining = Math.max(0, exp - nowSeconds);
            setSessionRemainingSeconds(remaining);

            if (remaining <= 0) {
                redirectToLogin();
                return;
            }

            const dismissedThisToken = sessionWarningDismissedForExpRef.current === exp;
            if (remaining <= SESSION_WARNING_SECONDS && !dismissedThisToken) {
                setSessionWarningOpen(true);
            } else if (remaining > SESSION_WARNING_SECONDS) {
                setSessionWarningOpen(false);
            }
        };

        evaluateExpiration();
        const intervalId = window.setInterval(evaluateExpiration, 1000);
        const onVisibility = () => {
            if (document.visibilityState === "visible") evaluateExpiration();
        };
        const onFocus = () => evaluateExpiration();
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", onFocus);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", onFocus);
        };
    }, [token, decodeJwtPayload, redirectToLogin]);

    const postponeSessionRenewal = useCallback(() => {
        const payload = decodeJwtPayload(localStorage.getItem("token") || "");
        if (payload?.exp) sessionWarningDismissedForExpRef.current = Number(payload.exp);
        setSessionWarningOpen(false);
    }, [decodeJwtPayload]);

    const renewSession = useCallback(async () => {
        const currentToken = localStorage.getItem("token");
        if (!currentToken || sessionRenewing) return;
        setSessionRenewing(true);
        try {
            const { data } = await axios.post(`${API}/auth/refresh-session`, {}, {
                headers: { Authorization: `Bearer ${currentToken}` },
                withCredentials: true,
            });
            if (!data?.token) throw new Error("El backend no devolvió un token renovado");
            sessionWarningDismissedForExpRef.current = null;
            setToken(data.token);
            setSessionWarningOpen(false);
        } catch (error) {
            const status = error?.response?.status;
            if (status === 401 || status === 419) redirectToLogin();
            else console.warn("[session refresh] No se pudo renovar la sesión", error);
        } finally {
            setSessionRenewing(false);
        }
    }, [sessionRenewing, redirectToLogin]);

    // ======= 🔔 Sincronizar logout entre pestañas =======
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === "token" && e.storageArea === localStorage && !e.newValue) {
                logout();
                if (!window.location.pathname.startsWith("/evaluation-system-verify")) {
                    window.location.replace(LOGIN_PATH);
                }
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [logout]);

    // ======= 🛡️ Interceptores Axios para token expirado =======
    useEffect(() => {
        const reqId = axios.interceptors.request.use(
            (config) => {
                const t = localStorage.getItem("token");
                if (t) config.headers.Authorization = `Bearer ${t}`;
                return config;
            },
            (error) => Promise.reject(error)
        );

        const resId = axios.interceptors.response.use(
            (r) => r,
            (error) => {
                const status = error?.response?.status;
                const msg = error?.response?.data?.msg;
                const tokenExpired =
                    status === 401 ||
                    status === 419 ||
                    msg === "errorRefresh" ||
                    msg === "tokenExpired";

                if (tokenExpired) {
                    logout();
                    if (!window.location.pathname.startsWith("/evaluation-system-verify")) {
                        window.location.replace(LOGIN_PATH);
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(reqId);
            axios.interceptors.response.eject(resId);
        };
    }, [logout]);

    const value = useMemo(
        () => ({
            token,
            setToken,
            isAuthReady,
            logout,
            user,
            setUser,
            name,
            setName,
            fullName,
            setFullName,
            rol,
            setRol,
            sedeId,
            sedeName,
            sedeLocation,
            refreshSede,
            applySede,
            showSedeLoginNotice,
        }),
        [
            token,
            isAuthReady,
            logout,
            user,
            name,
            fullName,
            rol,
            sedeId,
            sedeName,
            sedeLocation,
            refreshSede,
            applySede,
            showSedeLoginNotice,
        ]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
            <SedeSessionNotice notice={sedeNotice} onClose={closeSedeNotice} />
            <FmReminderModal reminder={fmReminder} onClose={closeFmReminder} />
            <SessionExpiryModal
                open={sessionWarningOpen}
                remainingSeconds={sessionRemainingSeconds}
                renewing={sessionRenewing}
                onContinue={renewSession}
                onLater={postponeSessionRenewal}
                onLogout={redirectToLogin}
            />
        </AuthContext.Provider>
    );
}
